import { Fragment } from 'react';

import { Image } from 'expo-image';
import { useAppTakeoverCartItem } from 'features/app-takeover/hooks/use-app-takeover';
import { useAppTakeoverCart } from 'features/app-takeover/hooks/use-app-takeover-cart';
import { useCartOfferValidation } from 'features/cart/hooks/use-cart-offer-validation';
import { commonStyles } from 'features/cart/styles';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { ImageBackground, StyleSheet, View } from 'react-native';
import { Typography } from 'ucl';
import { Icon } from 'ucl/components/icon/Icon';

import { Skeleton } from 'components/ui/skeleton';
import { WalmartBadgeContainer } from 'pages/cart/cart-item/cart-item-offer';
import { t } from 'state/intl/translations';
import { primitive } from 'styles/constants';

import { CartEntryFabButton } from '../cart-actions/cart-entry-fab-button';

const IncentiveHeaderOfferFeedback = ({ cartEntry }: { cartEntry: ICartEntry }) => {
  const { feedbackMessages, isOfferApplied, isLoading } = useCartOfferValidation(cartEntry);

  const offerMessageKey = isOfferApplied ? 'offerApplied' : 'offerNotApplied';
  const offerMessage = t(offerMessageKey);
  const textSeparator = ' - ';

  if (isLoading) {
    return <Skeleton shape="rectangle" style={{ width: '80%', height: 12, marginVertical: 0 }} />;
  }

  return (
    <View style={{ gap: 2, flexDirection: 'row', alignItems: 'center' }}>
      <Icon marginRight={1} variant={isOfferApplied ? 'checkFill' : 'errorV2'} size="3" />
      <Typography style={styles.feedbackText}>
        {offerMessage}
        {feedbackMessages?.map((message, index) => (
          <Fragment key={index}>
            {textSeparator}
            {message}
          </Fragment>
        ))}
      </Typography>
    </View>
  );
};

const getWrappedComboBenefit = (cartEntry: ICartEntry) => {
  const benefitEntry = cartEntry.children?.[0];
  return benefitEntry?.type === MinimalCartEntryType.combo ? benefitEntry : undefined;
};

const CartEntryIncentiveHeader = ({
  cartEntry,
  isReadOnly,
}: {
  cartEntry: ICartEntry;
  isReadOnly: boolean;
}) => {
  const { isOfferApplied, isLoading, walmart } = useCartOfferValidation(cartEntry);
  const comboBenefit = getWrappedComboBenefit(cartEntry);
  const comboTakeoverConfig = useAppTakeoverCartItem(comboBenefit?.url || '');
  const displayName = cartEntry.name || comboBenefit?.name || '';

  let headerBackgroundColor: string;
  if (isLoading) {
    headerBackgroundColor = primitive.$blackOpacity10;
  } else {
    headerBackgroundColor = isOfferApplied ? primitive.$successLight : primitive.$errorLight;
  }

  if (isReadOnly) {
    if (comboBenefit && comboTakeoverConfig?.cartItemImage) {
      return <CartEntryTakeoverHeader cartEntry={comboBenefit} />;
    }

    return (
      <View style={styles.header}>
        <Typography style={commonStyles.cartEntryItemName}>{displayName}</Typography>
      </View>
    );
  }

  if (comboBenefit && comboTakeoverConfig?.cartItemImage) {
    return <CartEntryTakeoverHeader cartEntry={comboBenefit} />;
  }

  return (
    <View
      style={[
        styles.header,
        {
          gap: 2,
          flexDirection: 'column',
          alignItems: 'flex-start',
          backgroundColor: headerBackgroundColor,
        },
      ]}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        {(walmart.isOfferSelected || walmart.isLoading) && <WalmartBadgeContainer />}
        <Typography style={commonStyles.cartEntryItemName}>{displayName}</Typography>
      </View>
      <IncentiveHeaderOfferFeedback cartEntry={cartEntry} />
    </View>
  );
};

const CartEntryComboHeader = ({
  cartEntry,
  showFabButton = true,
}: {
  cartEntry: ICartEntry;
  showFabButton?: boolean;
}) => {
  const itemTakeoverConfig = useAppTakeoverCartItem(cartEntry.url);

  return (
    <>
      {showFabButton && <CartEntryFabButton cartEntry={cartEntry} />}
      {itemTakeoverConfig?.cartItemImage ? (
        <CartEntryTakeoverHeader cartEntry={cartEntry} />
      ) : (
        <View style={styles.header}>
          <Typography style={commonStyles.cartEntryItemName}>{cartEntry.name}</Typography>
        </View>
      )}
    </>
  );
};

const CartEntryRewardHeader = ({ cartEntry }: { cartEntry: ICartEntry }) => {
  const rewardBenefit = cartEntry.children?.[0];
  if (!rewardBenefit) {
    return null;
  }
  if (rewardBenefit.type === MinimalCartEntryType.combo) {
    return <CartEntryComboHeader cartEntry={rewardBenefit} showFabButton={false} />;
  }
  return null;
};

export const CartEntryTakeoverHeader = ({ cartEntry }: { cartEntry: ICartEntry }) => {
  const { fontFamily } = useAppTakeoverCart();
  const itemTakeoverConfig = useAppTakeoverCartItem(cartEntry.url);

  if (!itemTakeoverConfig?.cartItemImage) {
    return null;
  }

  const content = (
    <>
      <Image source={itemTakeoverConfig.cartItemImage} style={styles.takeoverImage} />
      <Typography
        style={[commonStyles.cartEntryItemName, fontFamily ? { fontFamily } : undefined]}
        testID="cart-entry-takeover-title"
      >
        {cartEntry.name}
      </Typography>
    </>
  );

  if (itemTakeoverConfig.cartItemBackgroundImage) {
    return (
      <ImageBackground
        imageStyle={styles.headerBackgroundImage}
        source={itemTakeoverConfig.cartItemBackgroundImage}
        resizeMode="stretch"
        style={styles.header}
        testID="cart-entry-takeover-header"
      >
        {content}
      </ImageBackground>
    );
  }

  return (
    <View style={styles.header} testID="cart-entry-takeover-header">
      {content}
    </View>
  );
};

export const CartEntryHeader = ({
  cartEntry,
  readOnly = false,
}: {
  cartEntry: ICartEntry;
  readOnly?: boolean;
}) => {
  if (cartEntry.type === MinimalCartEntryType.reward) {
    return <CartEntryRewardHeader cartEntry={cartEntry} />;
  }

  if (cartEntry.type === MinimalCartEntryType.combo) {
    return <CartEntryComboHeader cartEntry={cartEntry} showFabButton={!readOnly} />;
  }

  if (cartEntry.type === MinimalCartEntryType.incentive) {
    return <CartEntryIncentiveHeader cartEntry={cartEntry} isReadOnly={readOnly} />;
  }

  if (cartEntry.type === MinimalCartEntryType.item) {
    return <CartEntryTakeoverHeader cartEntry={cartEntry} />;
  }

  return null;
};

const styles = StyleSheet.create({
  header: {
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    overflow: 'hidden',
    ...commonStyles.withBorderBottom,
  },
  headerBackgroundImage: {
    width: '100%',
    height: '100%',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  feedbackText: {
    fontSize: 10,
    fontFamily: primitive.fontFamily.$body,
    color: primitive.$bbqBrown,
  },
  takeoverImage: {
    width: 50,
    height: 50,
  },
});
